/**
 * Line Items Tab
 *
 * Displays all contract line items including charges, agent actions, features, and pricing tiers
 * This shows the pricing data that was copied from the plan at contract creation
 */

import type { LineItemResponse } from '@monorepo/shared/schemas/contracts'
import { Loader2, Package } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { useContractLineItems } from '../../hooks/useContracts'

interface LineItemsTabProps {
  contractId: string
}

export function LineItemsTab({ contractId }: LineItemsTabProps) {
  const { data: lineItems, isLoading, error } = useContractLineItems(contractId)

  const groupLineItemsByCategory = (items: LineItemResponse[]) => {
    const groups: Record<string, LineItemResponse[]> = {
      recurring: [],
      setup: [],
      usage: [],
      agent_actions: [],
      features: [],
      other: [],
    }

    items.forEach(item => {
      const category = item.line_item_type
      if (groups[category]) {
        groups[category].push(item)
      } else if (category.startsWith('usage_')) {
        groups.usage.push(item)
      } else {
        groups.other.push(item)
      }
    })

    return groups
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-center'>
          <Loader2 className='h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground' />
          <p className='text-muted-foreground'>Loading line items...</p>
        </div>
      </div>
    )
  }

  if (error || !lineItems) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-center'>
          <p className='text-red-600 font-medium'>Failed to load line items</p>
          <p className='text-sm text-muted-foreground mt-1'>
            {error?.message || 'Line items not found'}
          </p>
        </div>
      </div>
    )
  }

  if (lineItems.length === 0) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-center'>
          <Package className='h-12 w-12 mx-auto mb-4 text-muted-foreground' />
          <p className='text-muted-foreground font-medium'>
            No line items found
          </p>
          <p className='text-sm text-muted-foreground mt-1'>
            This contract has no pricing line items
          </p>
        </div>
      </div>
    )
  }

  const groupedItems = groupLineItemsByCategory(lineItems)

  return (
    <div className='space-y-6'>
      {/* Recurring Charges */}
      {groupedItems.recurring.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className='text-lg'>Recurring Charges</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-3'>
              {groupedItems.recurring.map(item => (
                <LineItemCard key={item.id} item={item} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Setup/One-Time Charges */}
      {groupedItems.setup.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className='text-lg'>Setup Charges</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-3'>
              {groupedItems.setup.map(item => (
                <LineItemCard key={item.id} item={item} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Usage-Based Charges */}
      {groupedItems.usage.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className='text-lg'>Usage-Based Charges</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-3'>
              {groupedItems.usage.map(item => (
                <LineItemCard key={item.id} item={item} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Agent Actions */}
      {groupedItems.agent_actions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className='text-lg'>Agent Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-3'>
              {groupedItems.agent_actions.map(item => (
                <LineItemCard key={item.id} item={item} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* DEPRECATED: Features section hidden (see DEPRECATED.md) */}
      {/* {groupedItems.features.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className='text-lg'>Features</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-3'>
              {groupedItems.features.map(item => (
                <LineItemCard key={item.id} item={item} />
              ))}
            </div>
          </CardContent>
        </Card>
      )} */}

      {/* Other Line Items */}
      {groupedItems.other.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className='text-lg'>Other Line Items</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-3'>
              {groupedItems.other.map(item => (
                <LineItemCard key={item.id} item={item} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function LineItemCard({ item }: { item: LineItemResponse }) {
  const formatCurrency = (amount: number | string, currency: string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(numAmount)
  }

  const getLineItemTypeVariant = (type: string) => {
    switch (type) {
      case 'plan':
        return 'default'
      case 'addon':
        return 'secondary'
      case 'discount':
        return 'outline'
      default:
        return 'outline'
    }
  }

  return (
    <div className='border rounded-lg p-4 space-y-3'>
      <div className='flex items-start justify-between'>
        <div className='space-y-1'>
          <div className='flex items-center gap-2'>
            <h4 className='font-medium'>{item.name}</h4>
            <Badge variant={getLineItemTypeVariant(item.type)}>
              {item.type}
            </Badge>
            {item.is_override && <Badge variant='outline'>Custom</Badge>}
          </div>
          {item.description && (
            <p className='text-sm text-muted-foreground'>{item.description}</p>
          )}
        </div>
        <div className='text-right'>
          <div className='font-semibold'>
            {formatCurrency(item.unit_price || 0, item.currency || 'USD')}
          </div>
          {item.pricing_model && (
            <div className='text-xs text-muted-foreground capitalize'>
              {item.pricing_model.replace('_', ' ')}
            </div>
          )}
        </div>
      </div>

      {/* Additional Details */}
      <div className='grid grid-cols-2 md:grid-cols-4 gap-3 text-sm'>
        {item.included_quantity !== null &&
          item.included_quantity !== undefined &&
          item.included_quantity > 0 && (
            <div>
              <span className='text-muted-foreground'>Quantity:</span>{' '}
              <span className='font-medium'>{item.included_quantity}</span>
            </div>
          )}
        {item.included_units !== null &&
          item.included_units !== undefined &&
          item.included_units > 0 && (
            <div>
              <span className='text-muted-foreground'>Included Units:</span>{' '}
              <span className='font-medium'>{item.included_units}</span>
            </div>
          )}
        {item.minimum_commitment !== null &&
          item.minimum_commitment !== undefined &&
          item.minimum_commitment > 0 && (
            <div>
              <span className='text-muted-foreground'>Min Commitment:</span>{' '}
              <span className='font-medium'>{item.minimum_commitment}</span>
            </div>
          )}
        {item.billing_timing && (
          <div>
            <span className='text-muted-foreground'>Billing:</span>{' '}
            <span className='font-medium capitalize'>
              {item.billing_timing}
            </span>
          </div>
        )}
      </div>

      {/* Pricing Tiers (for tiered/volume pricing) */}
      {item.tiers && item.tiers.length > 0 && (
        <div className='border-t pt-3'>
          <h5 className='text-sm font-medium mb-2'>Pricing Tiers</h5>
          <div className='space-y-2'>
            {item.tiers.map(tier => (
              <div
                key={`${tier.from_unit}-${tier.to_unit}`}
                className='flex items-center justify-between text-sm bg-muted/50 rounded px-3 py-2'
              >
                <span className='text-muted-foreground'>
                  {tier.from_unit} - {tier.to_unit || '∞'} units
                </span>
                <span className='font-medium'>
                  {formatCurrency(tier.unit_price || 0, item.currency || 'USD')}{' '}
                  per unit
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Source Badge */}
      {item.original_price !== null &&
        item.original_price !== undefined &&
        item.original_price !== item.unit_price && (
          <div className='border-t pt-3 flex items-center justify-between text-sm'>
            <span className='text-muted-foreground'>Original Price:</span>
            <span className='line-through'>
              {formatCurrency(item.original_price, item.currency || 'USD')}
            </span>
          </div>
        )}
    </div>
  )
}
